export interface UserPreferences {
  lastOrganizationId?: string;
  lastOrganizationSlug?: string;
  todosEnabled?: boolean;
  critsEnabled?: boolean;
  retrosEnabled?: boolean;
}

const PREFERENCES_KEY = 'design-commons-preferences';

export function getUserPreferences(): UserPreferences {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const stored = localStorage.getItem(PREFERENCES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error reading user preferences:', error);
    return {};
  }
}

export function setUserPreferences(preferences: Partial<UserPreferences>): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const current = getUserPreferences();
    const updated = { ...current, ...preferences };
    localStorage.setItem(PREFERENCES_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving user preferences:', error);
  }
}

export function setLastOrganization(orgId: string, orgSlug: string): void {
  setUserPreferences({
    lastOrganizationId: orgId,
    lastOrganizationSlug: orgSlug
  });
}

export function getLastOrganization(): { id: string; slug: string } | null {
  const prefs = getUserPreferences();
  if (prefs.lastOrganizationId && prefs.lastOrganizationSlug) {
    return {
      id: prefs.lastOrganizationId,
      slug: prefs.lastOrganizationSlug
    };
  }
  return null;
}

export function clearOrganizationPreference(): void {
  setUserPreferences({
    lastOrganizationId: undefined,
    lastOrganizationSlug: undefined
  });
}

export function getTodosEnabled(): boolean {
  // Todos are now always enabled for everyone
  return true;
}

export function setTodosEnabled(enabled: boolean): void {
  // No-op: Todos are always enabled, but keeping function for backwards compatibility
  // Dispatch custom event so components can react to preference changes
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('todosEnabledChanged', { detail: { enabled: true } }));
  }
}

export function getCritsEnabled(): boolean {
  const prefs = getUserPreferences();
  return prefs.critsEnabled ?? false; // Default to disabled
}

export function setCritsEnabled(enabled: boolean): void {
  setUserPreferences({ critsEnabled: enabled });
  // Dispatch custom event so components can react to preference changes
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('critsEnabledChanged', { detail: { enabled } }));
  }
}

export function getRetrosEnabled(): boolean {
  const prefs = getUserPreferences();
  return prefs.retrosEnabled ?? false; // Default to disabled
}

export function setRetrosEnabled(enabled: boolean): void {
  setUserPreferences({ retrosEnabled: enabled });
  // Dispatch custom event so components can react to preference changes
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('retrosEnabledChanged', { detail: { enabled } }));
  }
}
